import apiClient from './api';
import {
  Branding,
  BrandingInput,
  BrandingAssetType,
  AssetUploadResult,
  TenantType,
} from '@/types/branding.types';

/**
 * Branding Service
 *
 * API client for whitelabel branding management
 */

/**
 * Get branding for a team
 */
export const getTeamBranding = async (teamId: number): Promise<Branding> => {
  const response = await apiClient.get(`/api/branding/team/${teamId}`);
  return response.data.data;
};

/**
 * Get branding for an agency
 */
export const getAgencyBranding = async (agencyId: number): Promise<Branding> => {
  const response = await apiClient.get(`/api/branding/agency/${agencyId}`);
  return response.data.data;
};

/**
 * Update team branding
 */
export const updateTeamBranding = async (
  teamId: number,
  input: BrandingInput
): Promise<Branding> => {
  const response = await apiClient.put(`/api/branding/team/${teamId}`, input);
  return response.data.data;
};

/**
 * Update agency branding
 */
export const updateAgencyBranding = async (
  agencyId: number,
  input: BrandingInput
): Promise<Branding> => {
  const response = await apiClient.put(`/api/branding/agency/${agencyId}`, input);
  return response.data.data;
};

/**
 * Upload branding asset
 */
export const uploadBrandingAsset = async (
  tenantType: TenantType,
  tenantId: number,
  assetType: BrandingAssetType,
  file: File
): Promise<AssetUploadResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const endpoint =
    tenantType === TenantType.TEAM
      ? `/api/branding/upload/team/${tenantId}/${assetType}`
      : `/api/branding/upload/agency/${tenantId}/${assetType}`;

  const response = await apiClient.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.data;
};

/**
 * Delete branding asset
 */
export const deleteBrandingAsset = async (
  tenantType: TenantType,
  tenantId: number,
  assetType: BrandingAssetType
): Promise<void> => {
  const endpoint =
    tenantType === TenantType.TEAM
      ? `/api/branding/upload/team/${tenantId}/${assetType}`
      : `/api/branding/upload/agency/${tenantId}/${assetType}`;

  await apiClient.delete(endpoint);
};

/**
 * Reset branding to defaults
 */
export const resetBranding = async (
  tenantType: TenantType,
  tenantId: number
): Promise<Branding> => {
  const endpoint =
    tenantType === TenantType.TEAM
      ? `/api/branding/team/${tenantId}/reset`
      : `/api/branding/agency/${tenantId}/reset`;

  const response = await apiClient.post(endpoint);
  return response.data.data;
};
